import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

// Generate or retrieve a visitor ID for analytics
const getVisitorId = (): string => {
  const storageKey = "visitor_id";
  let visitorId = localStorage.getItem(storageKey);
  
  if (!visitorId) {
    visitorId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(storageKey, visitorId);
  }
  
  return visitorId;
};

interface UsePageViewOptions {
  pagePath?: string;
  projectId?: string;
}

export const usePageView = (options: UsePageViewOptions = {}) => {
  const hasTracked = useRef(false);

  useEffect(() => {
    // Only track once per page load
    if (hasTracked.current) return;
    hasTracked.current = true;

    const trackPageView = async () => {
      try {
        const pagePath = options.pagePath || window.location.pathname;
        const visitorId = getVisitorId();
        const userAgent = navigator.userAgent;
        const referrer = document.referrer || null;

        await supabase.from("page_views").insert({
          page_path: pagePath,
          project_id: options.projectId || null,
          visitor_id: visitorId,
          user_agent: userAgent,
          referrer: referrer,
        });
      } catch (error) {
        // Silently fail - analytics shouldn't break the app
        console.debug("Page view tracking failed:", error);
      }
    };

    // Small delay to ensure page is loaded
    const timeoutId = setTimeout(trackPageView, 500);
    
    return () => clearTimeout(timeoutId);
  }, [options.pagePath, options.projectId]);
};

// Track a specific project view
export const trackProjectView = async (projectId: string) => {
  try {
    const visitorId = getVisitorId();
    const userAgent = navigator.userAgent;
    const referrer = document.referrer || null;

    await supabase.from("page_views").insert({
      page_path: `/project/${projectId}`,
      project_id: projectId,
      visitor_id: visitorId,
      user_agent: userAgent,
      referrer: referrer,
    });
  } catch (error) {
    console.debug("Project view tracking failed:", error);
  }
};
